import { useEffect, useState, useCallback } from "react"
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Layout, Avatar, Dropdown, Flex, Modal, Select, Divider, message, Tabs, Badge, Button, Spin, Tooltip } from "antd";
import { UserOutlined, BellOutlined, QuestionCircleOutlined, ControlOutlined, LogoutOutlined, SearchOutlined, MoreOutlined, ReloadOutlined, SyncOutlined, GlobalOutlined, } from "@ant-design/icons";
import axios from "../../axios-interceptor.js"
import VeoliaLogo from "./assets/VeoliaLogo";
import { fetchUserInfo, fetchCompanyLogo, fetchProfilePicture } from "../../redux/actions/userAction";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "./style/appHeader.css";
import { showLoader } from "../../utils/showloader.js";
import GlobalSearch from "./GlobalSearch.jsx";
import { useTenant } from "../../context/TenantContext.jsx";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { getStoredCompanyIdentifier, getTenantLoginPath } from "../../utils/companyIdentifier.js";
import { getLocalFormattedDate } from "../../utils/getLocalFormattedDate.js";
import { FORM_LANGUAGE_OPTIONS } from "../../utils/formTranslations.js";

const { Header } = Layout;
// const pagesWithSidebar = ["organisation-structure", "workflow-configuration", "home-currency"]
const applicationHelpUrl = "https://flxflow.com/help";

// Calculate banner height - should match BANNER_HEIGHT in PlanLimitBanner
const BANNER_HEIGHT = 44;

const headerStyle = {
	textAlign: "center",
	backgroundColor: "#fff",
	borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
	position: "sticky",
	top: 0, // Will be adjusted dynamically in the component
	zIndex: 498, // Below the PlanLimitBanner (which has 499)
};

const iconStyle = {
	cursor: "pointer",
	color: "#8c8c8c",
	backgroundColor: "transparent"
}

const BACKEND_API = process.env.REACT_APP_BACKEND_API;
let tenantId = localStorage.getItem("tenantId")

const AppHeader = (props) => {
	const { showSidebar } = props;
	const { t, i18n } = useTranslation();
	const tenantNavigate = useTenantNavigation();
	const { clearTenant } = useTenant();
	const dispatch = useDispatch();
	const [notifications, setnotification] = useState([]);
	const [loadingNotifications, setLoadingNotifications] = useState(false);
	const [openSettingsModal, setOpenSettingsModal] = useState(false);
	const [openNotificationModal, setOpenNotificationModal] = useState(false);
	const [activeTab, setActiveTab] = useState("");
	const showMainHeader = window.location.pathname.includes("admin") || window.location.pathname.includes("space") ? true : false;
	const currentPath = window.location.pathname;
	let { userInfo, profilePicture: profilePictureUrl } = useSelector(state => state.user);
	const { companyLogo } = useSelector(state => state.company);
	const { limits } = useSelector(state => state.plan);
	let showLogoOnTop = false
	const { spaceId } = useParams();
	const [selectedNotification, setSelectedNotification] = useState(null);
	// Responsive breakpoint detection - removed as we're using Tailwind CSS classes

	// Check if PlanLimitBanner is visible
	const currentSpacesCount = userInfo?.spaceInfo?.length || 0;
	const spacesLimit = limits?.spaces || 0;
	const isSpacesLimitReached = spacesLimit > 0 && currentSpacesCount >= spacesLimit;
	const isBannerVisible = userInfo?.isAdmin && isSpacesLimitReached;

	useEffect(() => {
		if (currentPath.includes("workflow")) {
			setActiveTab("workflow")
		}
		if (currentPath.includes("settings")) {
			setActiveTab("space-settings")
		}
	}, [currentPath])

	if (!showSidebar) {
		showLogoOnTop = true
	}

	const logoutHandler = () => {
		const companyIdentifier = getStoredCompanyIdentifier();
		clearTenant(); // Clears session but keeps companyIdentifier for tenant login
		window.location.href = getTenantLoginPath(companyIdentifier);
	}

	const settingsHandler = () => {
		// setOpenSettingsModal(true)
		tenantNavigate("/app/user/settings")
	}

	const adminControlsHandler = () => {
		tenantNavigate("/app/admin/controls")
	}

	const items = [
		{
			key: 1,
			label: <Flex gap={8}><UserOutlined /><span>{t("Account Settings")}</span></Flex>,
			onClick: settingsHandler
		},
		{
			key: 3,
			label: <Flex gap={8}><LogoutOutlined /><span>{t("Logout")}</span></Flex>,
			danger: true,
			onClick: logoutHandler
		},
	];
	if (userInfo.isAdmin) {
		items.splice(1, 0, {
			key: 2,
			label: <Flex gap={8}><ControlOutlined /><span>{t("Administrator Controls")}</span></Flex>,
			onClick: adminControlsHandler
		})
	}

	const onClick = () => {
		tenantNavigate("/app/dashboard")
	}

	const changeUserLang = async (val) => {
		// Apply the language to the UI immediately, then persist it to the user's
		// profile. `fetchUserInfo` refreshes userInfo.defaultLanguage so the
		// switcher value and the App-level i18n effect stay in sync.
		i18n.changeLanguage(val)
		showLoader(true)
		try {
			await axios({
				method: "PUT",
				url: `${BACKEND_API}/users`,
				data: {
					userId: userInfo.userId,
					defaultLanguage: val,
					tenantId: tenantId
				},
				headers: {
					Accept: "application/json",
					userId: userInfo.userId,
				},
			});
			message.success(t("Language updated successfully"));
			dispatch(fetchUserInfo());
			setOpenSettingsModal(false)
			// setOpenSettingsModal(false)
		} catch (error) {
		}
		showLoader(false)
	}

	const handleLangChange = async (val) => {
		Modal.confirm({
			title: t('Confirm'),
			content: t('Are you sure to change language?'),
			okText: t("Yes"),
			cancelText: t("No"),
			onOk: (event) => {
				changeUserLang(val)
				Modal.destroyAll()
			},
			footer: (_, { OkBtn, CancelBtn }) => (
				<>
					<CancelBtn />
					<OkBtn />
				</>
			),
		});

	}

	// Fetch notifications from API
	const fetchNotifications = useCallback(async () => {
		if (!userInfo?.userId) return;
		setLoadingNotifications(true);
		try {
			const response = await axios({
				method: "GET",
				url: `${BACKEND_API}/userNotifications`
			});
			if (response.data && response.data.result) {
				const notificationData = response.data.result.reverse()
				const transformedNotifications = notificationData.map((notification, index) => {
					const userLang = userInfo.defaultLanguage || 'english';
					const langSpecific = notification.languageSpecific || {};
					const availableLangs = Object.keys(langSpecific);
					const resolvedLang = langSpecific[userLang]
						? userLang
						: (langSpecific.english ? 'english' : availableLangs[0]);
					const content = resolvedLang ? langSpecific[resolvedLang] : undefined;

					// Extract subject (plain text)
					let subjectText = '';
					if (content?.subject) {
						const tempDiv = document.createElement('div');
						tempDiv.innerHTML = content.subject;
						subjectText = tempDiv.textContent || tempDiv.innerText || '';
						subjectText = subjectText
							.replace(/@Space name/g, 'Workflow Space')
							.replace(/@Document Id/g, 'Document')
							.trim();
					}

					// Extract body (HTML and plain text) — payload uses `content`; templates use `body`
					let bodyHtml = content?.content || content?.body || '';
					let bodyText = '';
					if (bodyHtml) {
						const tempDiv = document.createElement('div');
						tempDiv.innerHTML = bodyHtml;
						const titleTag = tempDiv.querySelector('[data-name="title-tag"]');
						if (titleTag) {
							bodyText = titleTag.textContent || titleTag.innerText || '';
							bodyText = bodyText.replace(/@Recipient/g, userInfo.name || 'User').trim();
						}
						if (!bodyText) {
							const bodyTag = tempDiv.querySelector('[data-name="body-tag"]');
							if (bodyTag) bodyText = bodyTag.textContent || bodyTag.innerText || '';
						}
						if (!bodyText) bodyText = tempDiv.textContent || tempDiv.innerText || '';
						bodyText = bodyText.replace(/\s+/g, ' ').replace(/@\w+/g, '').trim();
					}
					const truncatedBody = bodyText.length > 100 ? bodyText.substring(0, 100) + '…' : bodyText;

					const hoursAgo = index + 1;
					const createdDate = notification.updatedAt ? getLocalFormattedDate(notification.updatedAt) : ""
					// new Date(Date.now() - (hoursAgo * 60 * 60 * 1000));

					return {
						id: notification.notificationId,
						subject: subjectText,
						bodyHtml,
						bodyText,
						truncatedBody,
						createdOn: createdDate,
						redirectUrl: notification.redirectUrl,
						isRead: Math.random() > 0.7,
						type: "workflow",
						notificationType: notification.notificationType,
						enabled: notification.enabled,
						spaceId: notification.spaceId,
						status: notification.status
					};
				});
				transformedNotifications.sort((a, b) => b.createdOn - a.createdOn);
				setnotification(transformedNotifications);
			}
		} catch (error) {
			// message.error(t("Failed to fetch notifications"));
			setnotification([])
		} finally {
			setLoadingNotifications(false);
		}
	}, [userInfo?.userId, userInfo?.defaultLanguage, userInfo?.name, t]);

	// Fetch notifications when header loads
	useEffect(() => {
		if (userInfo?.userId) {
			fetchNotifications();
		}
	}, [userInfo?.userId, fetchNotifications]);

	// Load profile picture into Redux cache (no-op if already cached)
	useEffect(() => {
		if (userInfo?.userId && userInfo?.profile_picture && !profilePictureUrl) {
			dispatch(fetchProfilePicture(userInfo.userId, userInfo.profile_picture));
		} else if (!userInfo?.profile_picture) {
			// user has no profile picture — clear any stale cached value
			dispatch(fetchProfilePicture(null, null));
		}
	}, [userInfo?.userId, userInfo?.profile_picture, profilePictureUrl, dispatch]);

	// Load company logo for header brand (fallback to default if not set) - uses Redux cache
	useEffect(() => {
		// Only attempt fetch when we have a valid company name; otherwise the backend may return an unrelated record
		if (userInfo?.userId && userInfo?.company_name && !companyLogo) {
			dispatch(fetchCompanyLogo(userInfo.userId, userInfo?.company_name));
		}
	}, [userInfo?.userId, userInfo?.company_name, companyLogo, dispatch]);

	const notificationItem = [];
	notifications.forEach((n, nIndex) => {
		notificationItem.push({
			label: <div onClick={() => n.redirectUrl && tenantNavigate(n.redirectUrl)}>{n.subject}<div className="notification-timestamp">{dayjs(n.createdOn).format("YYYY/MM/DD h:m a")}</div></div>,
			key: String(nIndex),
		})
		if (nIndex !== (notifications.length - 1)) {
			notificationItem.push({
				type: "divider"
			})
		}
	})

	// Custom dropdown render with sticky footer
	const popupRender = (menu) => (
		<div className="notification-dropdown-wrapper">
			<div className="notification-dropdown-content">
				{loadingNotifications ? (
					<div style={{ padding: '24px', textAlign: 'center' }}>
						<Spin />
					</div>
				) : notifications.length === 0 ? (
					<div style={{ padding: '16px', textAlign: 'center', color: '#8c8c8c' }}>
						{t('No notifications')}
					</div>
				) : (
					<div style={{ maxHeight: '300px', overflowY: 'auto' }}>
						{menu}
					</div>
				)}
			</div>
			{notifications.length > 0 && (
				<div className="notification-dropdown-footer">
					<div
						style={{
							textAlign: 'center',
							color: '#1890ff',
							fontWeight: 'bold',
							padding: '8px 16px',
							cursor: 'pointer',
							borderTop: '1px solid #f0f0f0',
							backgroundColor: '#fff'
						}}
						onClick={() => setOpenNotificationModal(true)}
					>
						{t("View All Notifications")}
					</div>
				</div>
			)}		</div>
	);

	const tabItems = [
		{
			key: "space-settings",
			label: t("Space Setup"),
		},
		{
			key: "workflow",
			label: t("Workflow")
		}		
	];

	const handleTabChange = (key) => {
		tenantNavigate("/app/space/" + spaceId + "/" + key)
	}	// Mobile menu items - comprehensive menu for mobile devices
	const mobileMenuItems = [
		...(window.location.pathname.includes("dashboard") ? [{
			key: 'search',
			label: <Flex gap={8}><SearchOutlined /><span>{t("Search")}</span></Flex>,
			onClick: () => {
				// Add search functionality or modal here
			}
		}] : []),
		...(userInfo.isAdmin && currentPath.includes("dashboard") ? [{
			key: 'admin',
			label: <Flex gap={8}><ControlOutlined /><span>{t("Admin Controls")}</span></Flex>,
			onClick: () => {
				tenantNavigate("/app/admin/config");
			}
		}] : []),
		{
			key: 'help',
			label: <Flex gap={8}><Tooltip title={t('FlxFlow Guide')}><QuestionCircleOutlined /><span>{t("Help")}</span></Tooltip></Flex>,
			onClick: () => {
				// Add help functionality here
				window.open(applicationHelpUrl, '_blank');
			}
		}
	];

	const handleNotificationClick = (notification) => {
		setSelectedNotification(notification);
		setOpenNotificationModal(false);
	};

	const markAllAsRead = () => {
		setnotification(prev =>
			prev.map(notif => ({ ...notif, isRead: true }))
		)
	}

	const getNotificationIcon = (type) => {
		switch (type) {
			case 'workflow':
				return '⚡'
			case 'approval':
				return '⏰'
			case 'warning':
				return '⚠️'
			case 'success':
				return '✅'
			case 'info':
			default:
				return 'ℹ️'
		}
	}

	const refreshNotifications = () => {
		fetchNotifications();
	}

	const unreadCount = notifications.filter(n => !n.isRead).length

	return (
		<>
			{/* {
				showMainHeader ?  */}
			<Header
				style={{
					...headerStyle,
					top: isBannerVisible ? `${BANNER_HEIGHT}px` : 0,
					justifyContent: showSidebar ? "flex-end" : "space-between"
				}}
				key={showMainHeader}
				className="h-16 md:h-14 sm:h-12 lg:h-16 px-3 md:px-5 lg:px-10 flex flex-row items-center leading-14 md:leading-12 sm:leading-10 responsive-header main-app-header"
			>					<Flex
				align="center"
				gap={16}
				className={`header-left-section flex-1 ${showSidebar ? 'flex-none' : ''} gap-2 md:gap-4`}
			>
					{/* {showLogoOnTop ? <VeoliaLogo type={"long"} onClick={onClick} /> : <div style={{ width: "200px" }}></div>} */}						{showLogoOnTop ? <>
						<div className="md:hidden">
							<VeoliaLogo type="short" onClick={onClick} />
						</div>
						<div className="hidden md:block">
							<VeoliaLogo type="long" onClick={onClick} />
						</div>
						{/* <Divider type="vertical" className="hidden md:block md:h-14 sm:h-12 lg:h-16" /> */}
					</>
						: null
					}{window.location.pathname.includes("dashboard") ?
						<div className="hidden md:block">
							<GlobalSearch />
						</div> : null
					}						{(currentPath.includes("space-settings") || currentPath.includes("workflow")) ?
						<>
							<div className="hidden md:block">
								<Tabs
									className="space-header-tabs"
									defaultActiveKey="space-setup"
									activeKey={activeTab}
									onChange={handleTabChange}
									items={tabItems}
								/>
							</div>								<div className="block md:hidden">
								<Select
									value={activeTab}
									onChange={handleTabChange}
									className="min-w-[140px] w-auto"
									size="small"
									options={tabItems.map(item => ({
										value: item.key,
										label: item.label
									}))}
								/>
							</div>
						</> : null
					}
				</Flex>					<Flex
					align="center"
					gap={16}
					className="header-right-section gap-2 md:gap-4"
				>						{/* Language switcher - updates the user's default language */}
						<Select
							value={userInfo.defaultLanguage || i18n.language || "english"}
							onChange={changeUserLang}
							options={FORM_LANGUAGE_OPTIONS}
							size="small"
							variant="borderless"
							suffixIcon={<GlobalOutlined />}
							popupMatchSelectWidth={false}
							className="header-lang-select"
							style={{ minWidth: 96 }}
						/>
						{/* Desktop icons - show admin, search, help on desktop */}
					<div className="hidden md:flex items-center gap-4">
						{/* Admin icon - desktop only */}
						{userInfo.isAdmin && currentPath.includes("dashboard") && (
							<div
								className="admin-text text-bgcolor-base text-color-base px-3 py-1.5 text-sm cursor-pointer"
								onClick={() => tenantNavigate("/app/admin/config")}
							>
								<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
									<path d="M8.0005 15.1108C7.92655 15.1113 7.85351 15.0946 7.78716 15.0619L5.62272 13.9819C4.59729 13.472 3.73523 12.685 3.13428 11.7102C2.53334 10.7353 2.21755 9.61157 2.22272 8.46637V3.07082C2.2227 2.94622 2.27158 2.82658 2.35885 2.73765C2.44612 2.64872 2.56481 2.59759 2.68939 2.59526H4.23161C4.86249 2.59193 5.47928 2.40843 6.00939 2.06637L7.72939 0.96415C7.80574 0.915495 7.8944 0.889648 7.98494 0.889648C8.07548 0.889648 8.16414 0.915495 8.2405 0.96415L9.9605 2.06637C10.4906 2.40843 11.1074 2.59193 11.7383 2.59526H13.2805C13.3448 2.59225 13.409 2.60233 13.4693 2.62488C13.5296 2.64743 13.5847 2.68198 13.6312 2.72644C13.6778 2.7709 13.7148 2.82435 13.7401 2.88355C13.7653 2.94274 13.7783 3.00645 13.7783 3.07082V8.46637C13.7818 9.61175 13.4643 10.7352 12.8618 11.7093C12.2592 12.6834 11.3958 13.4692 10.3694 13.9775L8.20494 15.0575C8.14165 15.0904 8.07178 15.1086 8.0005 15.1108ZM3.16494 3.55526V8.46637C3.16223 9.43535 3.43102 10.3857 3.94083 11.2097C4.45064 12.0338 5.18109 12.6985 6.04939 13.1286L8.0005 14.1064L9.95161 13.1286C10.8199 12.6985 11.5504 12.0338 12.0602 11.2097C12.57 10.3857 12.8388 9.43535 12.8361 8.46637V3.55526H11.7694C10.9511 3.5552 10.1502 3.3191 9.46272 2.87526L8.0005 1.92415L6.53827 2.86637C5.85177 3.31334 5.05079 3.55255 4.23161 3.55526H3.16494Z" fill="#141BCB" />
									<path d="M8.00052 8.35553C7.57859 8.35553 7.16613 8.23042 6.81531 7.996C6.46448 7.76159 6.19105 7.42841 6.02958 7.03859C5.86811 6.64878 5.82586 6.21983 5.90818 5.80601C5.9905 5.39218 6.19368 5.01206 6.49203 4.71371C6.79038 4.41535 7.1705 4.21217 7.58433 4.12986C7.99816 4.04754 8.4271 4.08979 8.81691 4.25126C9.20673 4.41273 9.53991 4.68616 9.77432 5.03698C10.0087 5.38781 10.1339 5.80027 10.1339 6.2222C10.1327 6.78764 9.90754 7.32958 9.50772 7.7294C9.1079 8.12922 8.56596 8.35436 8.00052 8.35553ZM8.00052 5.03553C7.76582 5.03553 7.53639 5.10513 7.34125 5.23552C7.1461 5.36592 6.994 5.55125 6.90418 5.76808C6.81437 5.98492 6.79087 6.22352 6.83666 6.45371C6.88244 6.6839 6.99546 6.89534 7.16142 7.0613C7.32738 7.22726 7.53882 7.34028 7.76901 7.38607C7.99921 7.43185 8.2378 7.40835 8.45464 7.31854C8.67147 7.22872 8.85681 7.07662 8.9872 6.88148C9.11759 6.68633 9.18719 6.4569 9.18719 6.2222C9.18601 5.90825 9.06047 5.60756 8.83805 5.38597C8.61564 5.16439 8.31448 5.03998 8.00052 5.03998V5.03553Z" fill="#141BCB" />
									<path d="M10.8451 10.9646H9.89847C9.8973 10.7139 9.79719 10.4738 9.61991 10.2965C9.44263 10.1192 9.20252 10.0191 8.95181 10.018H7.04958C6.79851 10.018 6.55772 10.1177 6.38019 10.2952C6.20265 10.4728 6.10292 10.7136 6.10292 10.9646H5.15625C5.15625 10.4625 5.35573 9.9809 5.71079 9.62583C6.06586 9.27076 6.54744 9.07129 7.04958 9.07129H8.94736C9.44991 9.07129 9.93195 9.27062 10.2877 9.62556C10.6435 9.9805 10.844 10.4621 10.8451 10.9646Z" fill="#141BCB" />
								</svg>
								{t("Admin")}
							</div>
						)}

						{/* Help icon - desktop only */}
						<Avatar
							icon={<Flex gap={8}><Tooltip title={t('FlxFlow Guide')}><QuestionCircleOutlined /></Tooltip></Flex>}
							style={iconStyle}
							// className="w-10 h-10"
							onClick={() => {
								// Add help functionality here
								window.open(applicationHelpUrl, '_blank');
							}}
						/>
					</div>						{/* Notification Dropdown */}
					<Dropdown
						overlayClassName="notification-dropdown"
						menu={{ items: notificationItem }}
						trigger={['click']}
						overlayStyle={{ maxWidth: 300 }}
						className="max-w-[280px] md:max-w-[300px]"
						popupRender={popupRender}
					>
						<div style={{ position: 'relative', display: 'inline-block' }}>
							<Avatar
								style={{ ...iconStyle, cursor: 'pointer' }}
								icon={<BellOutlined className="w-5 h-5 md:w-6 md:h-6" />}
								onClick={fetchNotifications}
							/>
						</div>
					</Dropdown>

					{/* <Divider type="vertical" className="hidden md:block md:h-14 sm:h-12 lg:h-16" /> */}

					{/* User Profile Dropdown */}
					<div className="user-profile-box">
						<img
							src={companyLogo || "/flxflow_logo.svg"}
							alt={companyLogo ? "Company Logo" : "FlxFlow Logo"}
							className="h-[37px]"
						/>
						<Dropdown overlayClassName="user-account" menu={{ items: items }} placement="bottomLeft" >
							<a
								onClick={(e) => e.preventDefault()}
								href="/#"
							>
								<Avatar
									src={profilePictureUrl || undefined}
									icon={!profilePictureUrl ? <div className="text-color-base">{userInfo?.name?.[0]}</div> : null}
									className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center"
									style={{
										color: "white",
										backgroundColor: profilePictureUrl ? undefined : "#e8e8fa"
									}}
								/>
							</a>
						</Dropdown>

					</div>
					{/* Mobile More Menu - placed after profile icon */}
					{mobileMenuItems.length > 0 && (
						<Dropdown
							menu={{ items: mobileMenuItems }}
							trigger={['click']}
							overlayClassName="mobile-menu-dropdown md:hidden"
							placement="bottomRight"
						>
							<Avatar
								className="w-8 h-8 cursor-pointer md:hidden"
								style={iconStyle}
								icon={<MoreOutlined className="w-5 h-5" />}
							/>
						</Dropdown>
					)}

					{/* <div className="text-color-primary text-regular user-name-text hidden md:block">{userInfo.name}</div> */}

				</Flex>
			</Header>

			{/* } */}<Modal title={t("Settings")} className="settings-modal" open={openSettingsModal} onCancel={() => setOpenSettingsModal(false)} onOk={() => setOpenSettingsModal(false)}>
				<div className="settings-container">
					<div className="text-h2 text-weight-600 text-color-primary">{userInfo.name}</div>
					<div className="text-h3 text-weight-400 text-color-primary">{userInfo.email}</div>
					<Divider />
					<div style={{ marginTop: "12px", marginBottom: "5px" }}><label>{t("Default Language")}</label></div>
					<Select placeholder={t('Select your default language')} onChange={handleLangChange} value={userInfo.defaultLanguage} style={{ width: "100%" }} options={FORM_LANGUAGE_OPTIONS} />
				</div>
			</Modal>			<Modal
				title={
					<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
						<span>
							{t("All Notifications")}
							{unreadCount > 0 && (
								<Badge
									count={unreadCount}
									style={{ marginLeft: '8px' }}
									size="small"
								/>
							)}
						</span>
						<div style={{ display: 'flex', gap: '8px' }}>
							<Button
								icon={<ReloadOutlined />}
								size="small"
								loading={loadingNotifications}
								onClick={refreshNotifications}
								title={t("Refresh notifications")}
							/>
							{unreadCount > 0 && (
								<span
									style={{ cursor: 'pointer', color: '#1890ff', fontSize: '14px' }}
									onClick={markAllAsRead}
								>
									{t('Mark all as read')}
								</span>
							)}
						</div>
					</div>
				} className="notification-modal w-[90vw] max-w-[500px]"
				open={openNotificationModal}
				onCancel={() => setOpenNotificationModal(false)}
				footer={null}
			>
				<div className="notification-container max-h-[400px] overflow-y-auto">					{notifications.length === 0 ? (
					<div className="text-center py-5 text-gray-500">
						{loadingNotifications ? 'Loading notifications...' : 'No notifications'}
					</div>
				) : (notifications.map((notification, index) => (
					<div
						key={notification.id || notification.notificationId || `${notification.createdOn || 'notification'}-${index}`}
						className={`px-4 py-3 cursor-pointer transition-colors duration-200 ${notification.isRead ? 'bg-white' : 'bg-blue-50'
							} ${index < notifications.length - 1 ? 'border-b border-gray-100' : ''
							} ${notification.redirectUrl ? 'hover:bg-blue-100' : ''
							}`}
						onClick={() => handleNotificationClick(notification)}
					>								<div className="flex items-start gap-2">
							<span className="text-base mt-0.5">
								{getNotificationIcon(notification.type)}
							</span>
							<div className="flex-1">
								<div className={`text-sm leading-relaxed text-gray-800 ${notification.isRead ? 'font-normal' : 'font-medium'
									}`}>
									{notification.subject}
								</div>
								<div className="text-xs text-gray-500 mt-1">
									{notification.truncatedBody}
								</div>
								<div className="text-xs text-gray-500 mt-1">
									{dayjs(notification.createdOn).format("MMM DD, YYYY h:mm A")}
								</div>
								{notification.notificationType && (
									<div className="text-xs text-blue-600 mt-1">
										{notification.notificationType.replace(/_/g, ' ')}
									</div>
								)}
							</div>
							{!notification.isRead && (
								<div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5" />
							)}
						</div>
					</div>
				))
				)}
				</div>
			</Modal>

			{/* Full notification modal */}
			<Modal
				open={!!selectedNotification}
				title={selectedNotification?.subject}
				onCancel={() => setSelectedNotification(null)}
				footer={null}
				centered
			>
				{selectedNotification && (
					<div>
						<div className="text-xs text-gray-500 mb-2">{dayjs(selectedNotification.createdOn).format("MMM DD, YYYY h:mm A")}</div>
						<div className="mb-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedNotification.bodyHtml) }} />
						{selectedNotification.redirectUrl && (
							<a href={selectedNotification.redirectUrl} target="_blank" rel="noopener noreferrer">
								<button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition">{t('Go to Request')}</button>
							</a>
						)}
					</div>
				)}
			</Modal>
		</>
	);
};

export default AppHeader;
